//Components.
import React from 'react';
import PropTypes from 'prop-types';
import { FormControl } from 'react-bootstrap';
import { change } from 'redux-form';
import { connect } from 'react-redux';
import cx from 'classnames';
import moment from 'moment';

//Action and helpers.
import { checkAvailability } from '../../../actions/checkAvailability';

// Helper function to check if a time is reserved (for start time selection)
// A start time is blocked if:
// 1. It falls within an existing reservation interval
// 2. Or the entire day is blocked by a daily reservation
// @param isNextDay - if true, the time belongs to the day after selectedDate
function isTimeReserved(selectedDate, time, reservedSlots = [], isNextDay = false) {
    if (!selectedDate) return false;
    
    // IMPORTANT: If this time option is marked as next day (+1), 
    // we need to check against the NEXT day's reservations, not the selected date
    const actualDate = isNextDay 
        ? moment(selectedDate).add(1, 'day') 
        : moment(selectedDate);
    
    const selectedDateStr = actualDate.format('YYYY-MM-DD');
    const prevDayStr = actualDate.clone().subtract(1, 'day').format('YYYY-MM-DD');
    const timeValue = parseFloat(time);
    
    return reservedSlots.some(slot => {
        const slotDate = moment(slot.checkIn).format('YYYY-MM-DD');
        const slotStart = parseFloat(slot.startTime);
        const slotEnd = parseFloat(slot.endTime);
        const slotCrossesMidnight = slotEnd < slotStart;
        
        // For daily bookings on the same date, block all times
        if (slotDate === selectedDateStr && !slot.isHourly) {
            return true;
        }
        
        // For hourly bookings
        if (slot.isHourly) {
            // Same day reservation
            if (slotDate === selectedDateStr) {
                if (!slotCrossesMidnight) {
                    // Normal same-day slot: block times within [slotStart, slotEnd)
                    if (timeValue >= slotStart && timeValue < slotEnd) {
                        return true;
                    }
                } else {
                    // Slot crosses midnight (e.g., 22:00 - 02:00)
                    // Block from slotStart to midnight
                    if (timeValue >= slotStart) {
                        return true;
                    }
                }
            }
            
            // Previous day reservation that crosses midnight into today
            if (slotDate === prevDayStr && slotCrossesMidnight) {
                // Block from midnight to slotEnd
                if (timeValue < slotEnd) {
                    return true;
                }
            }
        }
        
        return false;
    });
}

class TimeField extends React.Component {
    static propTypes = {
        startTime: PropTypes.number.isRequired,
        endTime: PropTypes.number.isRequired,
        checkAvailability: PropTypes.func.isRequired,
        listId: PropTypes.number.isRequired,
        formName: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        reservedSlots: PropTypes.array,
    };

    static defaultProps = {
        reservedSlots: [],
    };

    constructor(props) {
        super(props);
        this.state = {
            inputValue: props.value
        };
        this.handleChange = this.handleChange.bind(this);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const { value } = nextProps;
        this.state = {
            inputValue: value
        };
    }

    async handleChange(event) {
        let value = event.target.value;
        const { name, formName, maximumNights, minimumNights } = this.props;
        const { listId, checkAvailability, change } = this.props;
        const { startDate, endDate, startTime, endTime } = this.props;

        this.setState({
            inputValue: value
        })
        await change(formName, name, value);
        if (name === "endTime" && startDate) {
            checkAvailability(listId, startDate, endDate, maximumNights, startTime, value, minimumNights);
        } else if (name === "startTime" && startDate) {
            if (startDate && endDate && endTime) {
                checkAvailability(listId, startDate, endDate, maximumNights, value, endTime, minimumNights);
            }
        }
    }

    render() {
        const { name, className, TimeLookup, classNameParent, label, reservedSlots, startDate } = this.props;
        return (
            <div className={cx('inputFocusColor', classNameParent)}>
                <FormControl
                    name={name}
                    componentClass="select"
                    className={className}
                    onChange={this.handleChange}
                    value={this.state.inputValue}
                >
                    <option value="">{label}</option>
                    {
                        TimeLookup && TimeLookup.length > 0 && TimeLookup.map((item, key) => {
                            // Pass isNextDay to check against the correct date
                            const disabled = isTimeReserved(startDate, item.value, reservedSlots, item.isNextDay);
                            return (
                                <option key={key} value={item.value} disabled={disabled}>
                                    {item.label}
                                </option>
                            );
                        })
                    }
                </FormControl>
            </div>
        );
    }
}

const mapState = state => ({
    isLoading: state.viewListing.isLoading,
    availability: state.viewListing.availability,
});

const mapDispatch = {
    checkAvailability,
    change,
};

export default connect(mapState, mapDispatch)(TimeField);
